import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Clock, Target } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { simulateMonth, computeService } from "@/lib/calc";
import { SectionCard, SectionTitle, InfoBox, ResultRow } from "@/components/ui-kit";
import { GuidedTip } from "@/components/GuidedTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const inputClass = "bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-2 text-center focus-visible:ring-2 focus-visible:ring-brand-champagne";

export default function SimulatorPage() {
  const { t, user, fmt, updateProfile } = useAuth();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const { data: costs = [] } = useQuery({ queryKey: ["fixed-costs"], queryFn: () => api.get("/fixed-costs").then((r) => r.data) });
  const [qty, setQty] = useState({});
  const [goal, setGoal] = useState(user.monthly_goal ?? "");
  const [savingGoal, setSavingGoal] = useState(false);

  const fixedTotal = costs.reduce((s, c) => s + Number(c.monthly_amount || 0), 0);
  const result = useMemo(() => simulateMonth(services, qty, user, fixedTotal), [services, qty, user, fixedTotal]);

  const goalNum = Number(goal) || 0;
  const diff = result.operatingResult - goalNum;
  const progress = goalNum > 0 ? Math.max(0, Math.min(100, (result.operatingResult / goalNum) * 100)) : 0;
  const saveGoal = async () => {
    setSavingGoal(true);
    try { await updateProfile({ monthly_goal: goalNum }); toast.success(t("goal.saved")); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setSavingGoal(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("simulator.title")}</h1>
        <p className="mt-1 text-brand-taupe">{t("simulator.subtitle")}</p>
      </div>

      {services.length > 0 && <GuidedTip id="operating-result">{t("tips.operatingResult")}</GuidedTip>}

      {services.length === 0 ? (
        <SectionCard accent className="text-center py-12"><p className="text-brand-taupe">{t("simulator.empty")}</p></SectionCard>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-3">
            {services.map((s) => {
              const c = computeService(s, user);
              const price = c.current ? c.current.price : c.commercial.nice;
              return (
                <SectionCard key={s.id} className="!p-4 flex items-center justify-between gap-4" data-testid={`sim-service-${s.id}`}>
                  <div className="flex-1">
                    <div className="font-medium text-brand-anthracite">{s.name}</div>
                    <div className="text-xs text-brand-taupe flex items-center gap-1"><Clock className="h-3 w-3" />{c.effectiveMin.toFixed(0)} {t("common.min")} · {fmt(price)}</div>
                  </div>
                  <div className="w-24">
                    <div className="text-[10px] uppercase tracking-wider text-brand-taupe text-center mb-1">{t("simulator.howMany")}</div>
                    <Input type="number" min="0" value={qty[s.id] ?? ""} onChange={(e) => setQty({ ...qty, [s.id]: e.target.value })} placeholder="0" className={inputClass} data-testid={`sim-qty-${s.id}`} />
                  </div>
                </SectionCard>
              );
            })}
          </div>

          <div className="lg:col-span-2 space-y-6 lg:sticky lg:top-6 lg:self-start">
            <SectionCard accent data-testid="sim-results">
              <SectionTitle>{t("simulator.title")}</SectionTitle>
              <ResultRow label={t("simulator.totalServices")} value={result.totalServices} />
              <ResultRow label={t("simulator.grossRevenue")} value={fmt(result.gross)} />
              <ResultRow label={t("simulator.netRevenue")} value={fmt(result.net)} />
              {user.vat_enabled && <ResultRow label={t("simulator.vat")} value={fmt(result.vat)} />}
              <ResultRow label={t("simulator.materialCosts")} value={fmt(result.material)} />
              <ResultRow label={t("simulator.timeValue")} value={fmt(result.timeValue)} />
              <ResultRow label={t("simulator.contribution")} value={fmt(result.contribution)} />
              <ResultRow label={t("simulator.fixedCosts")} value={fmt(result.fixedTotal)} />
              <div className="border-t border-brand-line mt-1 pt-1">
                <ResultRow label={t("simulator.operatingResult")} value={fmt(result.operatingResult)} strong tone={result.operatingResult >= 0 ? "success" : "danger"} />
              </div>
            </SectionCard>

            <SectionCard>
              <div className="flex items-center gap-2 text-brand-taupe mb-2"><Clock className="h-4 w-4" /><span className="text-xs uppercase tracking-wider">{t("simulator.workingHours")}</span></div>
              <div className="font-serif text-3xl font-semibold text-brand-anthracite" data-testid="sim-hours">{result.workingHours.toFixed(0)} {t("common.hours")}</div>
              <p className="mt-2 text-sm text-brand-taupe">{t("simulator.hoursMsg", { h: result.workingHours.toFixed(0) })}</p>
            </SectionCard>

            <SectionCard data-testid="sim-goal">
              <div className="flex items-center gap-2 mb-3"><Target className="h-4 w-4 text-brand-champagne" /><SectionTitle>{t("goal.label")}</SectionTitle></div>
              <GuidedTip id="monthly-goal" className="mb-3">{t("tips.monthlyGoal")}</GuidedTip>
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">{t("goal.yourGoal")}</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-taupe">{user.currency === "GBP" ? "£" : "€"}</span>
                    <Input type="number" min="0" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="0" className="bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-2 pl-8 focus-visible:ring-2 focus-visible:ring-brand-champagne" data-testid="sim-goal-input" />
                  </div>
                </div>
                <Button onClick={saveGoal} disabled={savingGoal} data-testid="sim-goal-save" className="rounded-xl bg-brand-anthracite text-brand-ivory h-10">{t("goal.save")}</Button>
              </div>
              {goalNum > 0 && (
                <div className="mt-4 space-y-3" data-testid="sim-goal-compare">
                  <div className="h-2 w-full rounded-full bg-brand-sand overflow-hidden">
                    <div className="h-full rounded-full bg-brand-champagne transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <ResultRow label={t("simulator.operatingResult")} value={fmt(result.operatingResult)} />
                  <ResultRow label={t("goal.yourGoal")} value={fmt(goalNum)} />
                  <ResultRow label={t("goal.difference")} value={fmt(diff)} strong tone={diff >= 0 ? "success" : "danger"} />
                  <p className="text-sm text-brand-anthracite" data-testid="sim-goal-message">
                    {diff >= 0 ? (diff < 0.01 ? t("goal.reached") : t("goal.exceeded", { amount: fmt(diff) })) : t("goal.away", { amount: fmt(-diff) })}
                  </p>
                </div>
              )}
            </SectionCard>

            <InfoBox>{t("simulator.resultDisclaimer")}</InfoBox>
          </div>
        </div>
      )}
    </div>
  );
}
